import React from 'react';
import { Box, HiddenFromView, Text } from '@stitch-fix/mode-react';
import classNames from 'classnames';
import { CloudinaryVideoPlayer } from '../../CloudinaryVideoPlayer';
import {
  GetStartedButton,
  type GetStartedButtonProps,
} from '../../GetStartedButton';
import styles from './styles.module.scss';
import {
  useUrlFromVideoBreakpoints,
  type VideoBreakpointProps,
} from '../utils';

export type VideoFullBleedHeroProps = {
  titleText?: string;
  titleShadow?: boolean;
  titleHiddenFromView?: boolean;
  contentOrder?: 'Title/CTA first' | 'Video first' | null;
  ctaText?: GetStartedButtonProps['copy'];
  ctaHref?: GetStartedButtonProps['href'];
} & VideoBreakpointProps;

export const VideoFullBleedHero = ({
  titleText,
  titleShadow,
  titleHiddenFromView,
  contentOrder,
  ctaText,
  ctaHref,
  videoDescription,
  mobileVideo,
  tabletVideo,
  desktopVideo,
  soundControls,
  captions,
  playPauseButtonSize,
}: VideoFullBleedHeroProps) => {
  const videoUrl = useUrlFromVideoBreakpoints({
    url: {
      mobile: mobileVideo,
      tablet: tabletVideo,
      desktop: desktopVideo,
    },
  });
  const videoAboveCTA = contentOrder === 'Video first';

  return (
    <Box
      className={classNames(styles.container, {
        [styles.videoFirst]: videoAboveCTA,
      })}
      data-testid="video-full-bleed-container"
    >
      <Box className={styles.textContainer}>
        {titleText && titleHiddenFromView && (
          <HiddenFromView>
            <h1 data-testid="hidden-from-view">{titleText}</h1>
          </HiddenFromView>
        )}
        {titleText && !titleHiddenFromView && (
          <Text
            as="h1"
            setting="title-xxlarge"
            className={classNames({ [styles.dropShadow]: titleShadow })}
            spacingBottom
          >
            {titleText}
          </Text>
        )}
        {(ctaText || ctaHref) && (
          <GetStartedButton
            copy={ctaText}
            href={ctaHref}
            eventLocation="hero"
          />
        )}
      </Box>
      <Box className={styles.videoContainer}>
        <CloudinaryVideoPlayer
          videoDescription={videoDescription}
          videoUrl={videoUrl}
          soundControls={soundControls}
          captions={captions}
          playPauseButtonSize={playPauseButtonSize}
        />
      </Box>
    </Box>
  );
};
